
import { Union, Record } from "../../fable_modules/fable-library-js.5.13.0/Types.js";
import { bool_type, union_type, bigint_type, array_type, uint32_type, record_type, int32_type } from "../../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { printf, toFail } from "../../fable_modules/fable-library-js.5.13.0/String.js";
import { initialize, item } from "../../fable_modules/fable-library-js.5.13.0/Array.js";
import { equals, op_LeftShift, op_Addition, fromZero, fromUInt32 } from "../../fable_modules/fable-library-js.5.13.0/BigInt.js";

/**
 * The window a slice covers: `SampleCount` samples, one every `Multiplier` clock cycles,
 * the first at clock cycle `StartSample * Multiplier`.
 * 
 * **StartSample counts samples, not clock cycles**, which is the waveform simulator's own
 * convention and the easiest thing here to get wrong: `WaveSimModel.StartCycle` is a DISPLAY
 * position, and the cycle it refers to is `StartCycle * SamplingZoom` - as
 * `lastCycleNeeded = (ShownCycles + StartCycle) * SamplingZoom` and
 * `CursorExactClkCycle = CursorDisplayCycle * SamplingZoom` both attest. The field is named
 * for what it counts so that a reader has to notice.
 */
export class Window$ extends Record {
    constructor(StartSample, Multiplier, SampleCount) {
        super();
        this.StartSample = (StartSample | 0);
        this.Multiplier = (Multiplier | 0);
        this.SampleCount = (SampleCount | 0);
    }
}

export function Window$_$reflection() {
    return record_type("WaveSlice.Window", [], Window$, () => [["StartSample", int32_type], ["Multiplier", int32_type], ["SampleCount", int32_type]]);
}

/**
 * Where a slice's samples live. Dense today - see the note above on runs. `Base` is the index
 * of sample 0 and `Stride` the distance between samples, so sample i is at Base + i*Stride:
 * local step data is read in place at the sampling multiplier, and a sidecar reply, already
 * sampled, at a stride of one.
 */
export class Samples extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Words", "Bigs"];
    }
}

export function Samples_$reflection() {
    return union_type("WaveSlice.Samples", [], Samples, () => [[["data", array_type(uint32_type)], ["Base", int32_type], ["Stride", int32_type]], [["data", array_type(bigint_type)], ["Base", int32_type], ["Stride", int32_type]]]);
}

export class WaveSlice extends Record {
    constructor(Window$, Width, Samples, HasLeadIn) {
        super();
        this.Window = Window$;
        this.Width = (Width | 0);
        this.Samples = Samples;
        this.HasLeadIn = HasLeadIn;
    }
}

export function WaveSlice_$reflection() {
    return record_type("WaveSlice.WaveSlice", [], WaveSlice, () => [["Window", Window$_$reflection()], ["Width", int32_type], ["Samples", Samples_$reflection()], ["HasLeadIn", bool_type]]);
}

/**
 * Sample i whatever the width, for the value readouts - the cursor column and the hover
 * tooltip - which want a number and not a representation.
 */
export function sampleValue(slice, i) {
    let slice_1, i_1, matchValue_1;
    if (slice.Samples.tag === 1) {
        const slice_2 = slice;
        const i_2 = i | 0;
        const matchValue_2 = slice_2.Samples;
        if (matchValue_2.tag === 0) {
            return toFail(printf("bigAt on a slice of width %d, which holds words"))(slice_2.Width);
        }
        else {
            return item(matchValue_2.fields[1] + ((((i_2 < 0) && !slice_2.HasLeadIn) ? 0 : i_2) * matchValue_2.fields[2]), matchValue_2.fields[0]);
        }
    }
    else {
        return fromUInt32((slice_1 = slice, (i_1 = (i | 0), (matchValue_1 = slice_1.Samples, (matchValue_1.tag === 1) ? toFail(printf("wordAt on a slice of width %d, which holds bigints"))(slice_1.Width) : item(matchValue_1.fields[1] + ((((i_1 < 0) && !slice_1.HasLeadIn) ? 0 : i_1) * matchValue_1.fields[2]), matchValue_1.fields[0])))));
    }
}

/**
 * A slice over a driver's local step data. Nothing is copied: the step arrays are read where
 * they lie, at the sampling stride.
 * 
 * `None` when the window is not inside what the simulation HOLDS - which is a question about
 * `clockTick`, not about the array's size. Those are different numbers and using the wrong one
 * is silent: an array long enough for a cycle the simulation has not reached yet holds zeros
 * there, and a slice over them draws zeros as confidently as data. This guarded on the array
 * length alone, and was right only because the caller happened to run the simulation first.
 * 
 * The step arrays are a circular buffer, and this reads them with a stride and no modulo, which
 * is only correct while the simulation has not wrapped. It never has here: the waveform
 * simulator sizes its arrays for the whole configured run. Rather than leave that as an
 * assumption two files apart, a wrapped simulation is refused - so if this is ever pointed at
 * the step simulator's arrays it says no instead of returning somebody else's cycles.
 */
export function ofLocalDriver(io, clockTick, window$) {
    let this$, this$_2, this$_3, this$_4, this$_5, this$_6, this$_7;
    const leadIn = window$.StartSample > 0;
    let firstNeeded;
    if (leadIn) {
        firstNeeded = (((this$ = window$, this$.StartSample * this$.Multiplier)) - window$.Multiplier);
    }
    else {
        const this$_1 = window$;
        firstNeeded = (this$_1.StartSample * this$_1.Multiplier);
    }
    if ((((((window$.SampleCount <= 0) ? true : (window$.Multiplier < 1)) ? true : (firstNeeded < 0)) ? true : (((this$_2 = window$, (this$_3 = this$_2, (this$_3.StartSample + (this$_2.SampleCount - 1)) * this$_3.Multiplier))) > clockTick)) ? true : (((this$_4 = window$, (this$_5 = this$_4, (this$_5.StartSample + (this$_4.SampleCount - 1)) * this$_5.Multiplier))) >= io.StepLength)) ? true : (clockTick >= io.StepLength)) {
        return undefined;
    }
    else {
        return new WaveSlice(window$, io.Width, (io.UInt32Slab.length > 0) ? (new Samples(/* Words */ 0, [io.UInt32Slab, io.StepBase + ((this$_6 = window$, this$_6.StartSample * this$_6.Multiplier)), window$.Multiplier])) : (new Samples(/* Bigs */ 1, [io.BigIntSlab, io.StepBase + ((this$_7 = window$, this$_7.StartSample * this$_7.Multiplier)), window$.Multiplier])), leadIn);
    }
}

/**
 * A slice over one signal's row of a sidecar reply, which is already sampled.
 * 
 * A reply carries `wordsPerSample` uint32s per sample, least significant word first, so a signal
 * of 32 bits or less is read in place at that stride - word 0 of each sample - and nothing is
 * copied. `rowBase` is the index of the row's first WORD, and `leadIn` says whether the fetch
 * asked for the extra sample before the window, in which case that sample is first and sample 0
 * is the next one.
 */
export function ofFetchedWords(data, rowBase, wordsPerSample, width, window$, leadIn) {
    return new WaveSlice(window$, width, new Samples(/* Words */ 0, [data, leadIn ? (rowBase + wordsPerSample) : rowBase, wordsPerSample]), leadIn);
}

/**
 * A slice over one signal's row of a sidecar reply, for a signal too wide for a uint32.
 * 
 * This one copies, because a bigint is not a view over anything: each sample's words are joined
 * into a number. It is the only copying reader here and it is affordable for the same reason the
 * width is unusual - a view draws tens of samples, not thousands.
 */
export function ofFetchedBigs(data, rowBase, wordsPerSample, width, window$, leadIn) {
    return new WaveSlice(window$, width, new Samples(/* Bigs */ 1, [initialize(window$.SampleCount + (leadIn ? 1 : 0), (i) => {
        const at = (rowBase + (i * wordsPerSample)) | 0;
        let v = fromZero();
        for (let w = wordsPerSample - 1; w >= 0; w--) {
            v = op_Addition(op_LeftShift(v, 32), fromUInt32(item(at + w, data)));
        }
        return v;
    }), leadIn ? 1 : 0, 1]), leadIn);
}

/**
 * Whether a binary waveform changes at the start of a clock cycle, and what it changes between.
 */
export class BinaryTransition extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["ZeroToZero", "ZeroToOne", "OneToZero", "OneToOne"];
    }
    static ZeroToZero = new BinaryTransition(0, []);
    static ZeroToOne = new BinaryTransition(1, []);
    static OneToZero = new BinaryTransition(2, []);
    static OneToOne = new BinaryTransition(3, []);
}

export function BinaryTransition_$reflection() {
    return union_type("WaveSlice.BinaryTransition", [], BinaryTransition, () => [[], [], [], []]);
}

/**
 * Whether a non-binary waveform changes value at the start of a clock cycle.
 */
export class NonBinaryTransition extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Change", "Const"];
    }
    static Change = new NonBinaryTransition(0, []);
    static Const = new NonBinaryTransition(1, []);
}

export function NonBinaryTransition_$reflection() {
    return union_type("WaveSlice.NonBinaryTransition", [], NonBinaryTransition, () => [[], []]);
}

/**
 * The transitions of a binary (one-bit) waveform, one per drawn cycle.
 */
export function binaryTransitions(slice) {
    return initialize(slice.Window.SampleCount, (i) => {
        let slice_1, i_1, matchValue, slice_2, i_2, matchValue_1;
        const matchValue_2 = (((slice_1 = slice, (i_1 = ((i - 1) | 0), (matchValue = slice_1.Samples, (matchValue.tag === 1) ? toFail(printf("wordAt on a slice of width %d, which holds bigints"))(slice_1.Width) : item(matchValue.fields[1] + ((((i_1 < 0) && !slice_1.HasLeadIn) ? 0 : i_1) * matchValue.fields[2]), matchValue.fields[0]))))) & 1) >>> 0;
        const matchValue_3 = (((slice_2 = slice, (i_2 = (i | 0), (matchValue_1 = slice_2.Samples, (matchValue_1.tag === 1) ? toFail(printf("wordAt on a slice of width %d, which holds bigints"))(slice_2.Width) : item(matchValue_1.fields[1] + ((((i_2 < 0) && !slice_2.HasLeadIn) ? 0 : i_2) * matchValue_1.fields[2]), matchValue_1.fields[0]))))) & 1) >>> 0;
        let matchResult;
        switch (matchValue_2) {
            case 0: {
                switch (matchValue_3) {
                    case 0: {
                        matchResult = 0;
                        break;
                    }
                    case 1: {
                        matchResult = 1;
                        break;
                    }
                    default:
                        matchResult = 3;
                }
                break;
            }
            case 1: {
                if (matchValue_3 === 0) {
                    matchResult = 2;
                }
                else {
                    matchResult = 3;
                }
                break;
            }
            default:
                matchResult = 3;
        }
        switch (matchResult) {
            case 0:
                return BinaryTransition.ZeroToZero;
            case 1:
                return BinaryTransition.ZeroToOne;
            case 2:
                return BinaryTransition.OneToZero;
            default:
                return BinaryTransition.OneToOne;
        }
    });
}

/**
 * The transitions of a wider waveform, with the sampled values beside them - the values are
 * what the viewer writes into each run, so they come back together rather than being read
 * twice.
 */
export function nonBinaryTransitionsWords(slice) {
    const values = initialize(slice.Window.SampleCount, (i) => {
        const slice_1 = slice;
        const i_1 = i | 0;
        const matchValue = slice_1.Samples;
        return (matchValue.tag === 1) ? toFail(printf("wordAt on a slice of width %d, which holds bigints"))(slice_1.Width) : item(matchValue.fields[1] + ((((i_1 < 0) && !slice_1.HasLeadIn) ? 0 : i_1) * matchValue.fields[2]), matchValue.fields[0]);
    }, Uint32Array);
    return [initialize(values.length, (i_2) => ((i_2 === 0) ? NonBinaryTransition.Change : ((item(i_2, values) === item(i_2 - 1, values)) ? NonBinaryTransition.Const : NonBinaryTransition.Change))), values];
}

/**
 * nonBinaryTransitionsWords for a slice wider than 32 bits.
 */
export function nonBinaryTransitionsBigs(slice) {
    const values = initialize(slice.Window.SampleCount, (i) => {
        const slice_1 = slice;
        const i_1 = i | 0;
        const matchValue = slice_1.Samples;
        return (matchValue.tag === 0) ? toFail(printf("bigAt on a slice of width %d, which holds words"))(slice_1.Width) : item(matchValue.fields[1] + ((((i_1 < 0) && !slice_1.HasLeadIn) ? 0 : i_1) * matchValue.fields[2]), matchValue.fields[0]);
    });
    return [initialize(values.length, (i_2) => ((i_2 === 0) ? NonBinaryTransition.Change : (equals(item(i_2, values), item(i_2 - 1, values)) ? NonBinaryTransition.Const : NonBinaryTransition.Change))), values];
}

